import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Flame, Send } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { ShareButton } from "@/components/ShareButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemorialView() {
  const { id } = useParams();
  const { t, fontClass } = useLang();
  const { playIndex, current } = usePlayer();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pirith, setPirith] = useState([]);
  const [trib, setTrib] = useState({ name: "", text: "" });

  const load = () => axios.get(`${API}/memorials/${id}`).then((r) => setM(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    axios.get(`${API}/songs`).then((r) => setPirith(r.data.filter((s) => (s.tags || []).includes("pirith")))).catch(() => {});
  }, [id]);

  const sadhu = async () => {
    try {
      const r = await axios.post(`${API}/memorials/${id}/sadhu`);
      setM((prev) => ({ ...prev, sadhu_count: r.data.sadhu_count }));
      if (pirith.length) playIndex(0, pirith);
      toast.success("සාදු! සාදු!! සාදු!!! 🙏");
    } catch (e) {}
  };

  const addTribute = async (e) => {
    e.preventDefault();
    if (!trib.name.trim()) { toast.error(t("rem_tribute_name")); return; }
    try {
      await axios.post(`${API}/memorials/${id}/tribute`, trib);
      setTrib({ name: "", text: "" }); load();
    } catch (err) { toast.error("Could not add."); }
  };

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/remembrance" data-testid="memorial-back-link" className="inline-flex items-center gap-2 font-ui font-bold text-sm" style={{ color: "var(--accent)" }}>
          <ArrowLeft size={18} /> {t("rem_nav")}
        </Link>
      </div>

      {loading ? (
        <p className="text-center font-ui py-16" style={{ color: "var(--text-muted)" }}>{t("loading")}</p>
      ) : !m ? (
        <p className="text-center font-ui py-16" style={{ color: "var(--text-muted)" }}>{t("rem_empty")}</p>
      ) : (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
          <div className="rounded-3xl p-8 text-center" style={{ background: "linear-gradient(160deg, #0C0A08 0%, #241a10 60%, #4A2E12 150%)", color: "#FFFDF7", border: "1px solid var(--accent-glow)" }} data-testid="memorial-view">
            {m.photo_url ? (
              <img src={m.photo_url} alt={m.name} className="w-28 h-28 rounded-full object-cover mx-auto" style={{ border: "3px solid var(--player-accent)" }} />
            ) : (
              <span className="grid place-items-center w-24 h-24 rounded-full mx-auto sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}><Flame size={40} /></span>
            )}
            <h1 className={`${fontClass()} text-2xl sm:text-3xl font-bold mt-4`}>{m.name}</h1>
            {m.date_of_passing && <p className="font-ui text-sm mt-1" style={{ color: "rgba(255,255,255,0.65)" }}>{m.date_of_passing}</p>}
            {m.note && <p className={`${fontClass()} text-base mt-3 leading-relaxed`} style={{ color: "rgba(255,255,255,0.85)" }}>{m.note}</p>}

            <button onClick={sadhu} data-testid="memorial-sadhu" className="mt-6 w-full h-14 rounded-full font-ui text-base font-bold flex items-center justify-center gap-2 sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}>
              <Flame size={22} /> {t("rem_sadhu")}
            </button>
            <p className="font-ui text-sm mt-3" style={{ color: "var(--player-accent)" }}>🪔 {m.sadhu_count} {t("rem_sadhu_count")}</p>

            <div className="mt-5 flex justify-center">
              <ShareButton testid="memorial-share" dark text={`🪔 ${m.name} — ${t("rem_title")}. පිං අනුමෝදන් වන්න 🙏`} />
            </div>
          </div>

          {/* tributes */}
          <div className="mt-6">
            <form onSubmit={addTribute} className="rounded-2xl p-4 space-y-2" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
              <p className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("rem_tribute")}</p>
              <input value={trib.name} onChange={(e) => setTrib({ ...trib, name: e.target.value })} placeholder={t("rem_tribute_name")} data-testid="tribute-name" className="w-full h-11 px-3 rounded-lg font-ui text-sm outline-none" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)" }} />
              <input value={trib.text} onChange={(e) => setTrib({ ...trib, text: e.target.value })} placeholder={t("rem_tribute_text")} data-testid="tribute-text" className="w-full h-11 px-3 rounded-lg font-ui text-sm outline-none" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)" }} />
              <button type="submit" data-testid="tribute-send" className="w-full h-11 rounded-lg font-ui font-bold text-sm flex items-center justify-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}><Send size={15} /> {t("rem_tribute_send")}</button>
            </form>
            <div className="mt-4 space-y-2">
              {(m.tributes || []).map((tr, i) => (
                <div key={i} className="rounded-xl p-3" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
                  <p className="font-ui text-sm font-bold" style={{ color: "var(--text)" }}>{tr.name} <span style={{ color: "var(--accent)" }}>🙏</span></p>
                  {tr.text && <p className={`${fontClass()} text-sm`} style={{ color: "var(--text-2)" }}>{tr.text}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
}
